import { useId, type ChangeEvent } from 'react'
import { Stack, Text } from '@n26/nxd'
import { useTranslation } from 'react-i18next'
import { ErrorMessage } from '@/shared/components/ErrorMessage'
import { TRANSFER_CURRENCY } from '@/features/transfer/model/constants'
import styles from './AmountField.module.css'

type AmountFieldProps = {
  value: string
  onChange: (next: string) => void
  label: string
  error?: string | null
  disabled?: boolean
  autoFocus?: boolean
}

const CURRENCY_SYMBOL: Record<string, string> = { EUR: '€' }

/** Keep only digits, a single separator, and up to two decimals. */
export const sanitizeAmount = (raw: string): string => {
  const cleaned = raw.replace(/[^0-9.,]/g, '')
  const [, whole = '', separator = '', rest = ''] =
    cleaned.match(/^(\d*)([.,])?(.*)$/) ?? []

  if (!separator) return whole
  const decimals = rest.replace(/\D/g, '').slice(0, 2)
  if (!whole) return decimals

  return `${whole}${separator}${decimals}`
}

export const AmountField = ({
  value,
  onChange,
  label,
  error,
  disabled = false,
  autoFocus = false,
}: AmountFieldProps) => {
  const { t } = useTranslation()
  const id = useId()
  const errorId = `${id}-error`

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(sanitizeAmount(event.target.value))
  }

  return (
    <Stack direction='vertical' space='16'>
      <label htmlFor={id}>
        <Text variant='standard-bold'>{label}</Text>
      </label>
      <div className={styles.row}>
        <span className={styles.currency} aria-hidden='true'>
          {CURRENCY_SYMBOL[TRANSFER_CURRENCY] ?? TRANSFER_CURRENCY}
        </span>
        <input
          id={id}
          className={styles.input}
          inputMode='decimal'
          autoComplete='off'
          placeholder={t('transfer.amount.placeholder')}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          // The amount is the primary action on this screen.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
        />
      </div>
      {error ? <ErrorMessage id={errorId}>{error}</ErrorMessage> : null}
    </Stack>
  )
}
